(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (e) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const addDays = (date, days) => {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + Number(days || 0));
    return copy;
  };

  const formatDate = (date) =>
    new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric'
    }).format(date);

  const formatRange = (start, end) =>
    `${formatDate(start)} – ${formatDate(end)}`;

  const tiers = {
    2: { discount: 10, progress: 33 },
    5: { discount: 15, progress: 66 },
    10: { discount: 20, progress: 100 }
  };

  const initDelivery = (root) => {
    if (!root) return;

    const today = new Date();

    const readyMin = Number(root.dataset.readyMin || 2);
    const readyMax = Number(root.dataset.readyMax || 4);
    const deliveryMin = Number(root.dataset.deliveryMin || 15);
    const deliveryMax = Number(root.dataset.deliveryMax || 18);

    const readyStart = addDays(today, readyMin);
    const readyEnd = addDays(today, readyMax);
    const deliveryStart = addDays(today, deliveryMin);
    const deliveryEnd = addDays(today, deliveryMax);

    const ordered = root.querySelector('[data-eden-date="ordered"]');
    const ready = root.querySelector('[data-eden-date="ready"]');
    const delivered = root.querySelector('[data-eden-date="delivered"]');
    const range = root.querySelector('[data-eden-delivery-range]');

    if (ordered) ordered.textContent = formatDate(today);
    if (ready) ready.textContent = formatRange(readyStart, readyEnd);

    const deliveryText = formatRange(deliveryStart, deliveryEnd);

    if (delivered) delivered.textContent = deliveryText;
    if (range) range.textContent = deliveryText;
  };

  const initBundle = (root) => {
    if (!root) return;

    const sectionId = root.dataset.sectionId;
    const formId = root.dataset.productFormId;
    const currency = root.dataset.currency || 'USD';

    let variants = [];

    try {
      variants = JSON.parse(
        root.querySelector('[data-eden-product-json]')?.textContent || '[]'
      );
    } catch (e) {
      variants = [];
    }

    const buttons = [...root.querySelectorAll('[data-bundle-qty]')];
    const badge = root.querySelector('[data-eden-bundle-badge]');
    const progress = root.querySelector('[data-eden-bundle-progress]');
    const message = root.querySelector('[data-eden-bundle-message]');
    const priceRow = root.querySelector('[data-eden-bundle-price]');
    const totalNode = root.querySelector('[data-eden-bundle-total]');
    const compareNode = root.querySelector('[data-eden-bundle-compare]');

    const productRoot =
      document.getElementById(`MainProduct-${sectionId}`) ||
      root.closest('product-info');

    const getQuantityInput = () =>
      document.getElementById(`Quantity-${sectionId}`) ||
      productRoot?.querySelector('input[name="quantity"]');

    const getProductForm = () =>
      document.getElementById(formId) ||
      productRoot?.querySelector(`form[id="${formId}"]`) ||
      productRoot?.querySelector('form[action*="/cart/add"]');

    const getVariantInput = () =>
      getProductForm()?.querySelector('input[name="id"]') ||
      productRoot?.querySelector('form[action*="/cart/add"] input[name="id"]');

    const getSelectedVariant = () => {
      const id = Number(getVariantInput()?.value);

      return (
        variants.find((variant) => Number(variant.id) === id) ||
        variants[0]
      );
    };

    let selectedBundleQty = null;

    const clearSelection = () => {
      selectedBundleQty = null;

      buttons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });

      if (badge) badge.textContent = 'Up to 20% off';
      if (progress) progress.style.width = '0%';
      if (message) message.textContent = 'Select a bundle to see your savings';

      if (priceRow) priceRow.hidden = true;
      if (compareNode) compareNode.textContent = '';
      if (totalNode) totalNode.textContent = '';
    };

    const renderSelectedBundle = () => {
      if (!selectedBundleQty) {
        clearSelection();
        return;
      }

      const tier = tiers[selectedBundleQty];
      const variant = getSelectedVariant();

      if (!tier || !variant) return;

      const originalTotal =
        Number(variant.price || 0) * selectedBundleQty;

      const discountedTotal =
        Math.round(originalTotal * (100 - tier.discount) / 100);

      buttons.forEach((button) => {
        const qty = Number(button.dataset.bundleQty);
        const active = qty === selectedBundleQty;

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (badge) {
        badge.textContent = `${tier.discount}% unlocked`;
      }

      if (progress) {
        progress.style.width = `${tier.progress}%`;
      }

      if (message) {
        if (selectedBundleQty === 10) {
          message.textContent =
            "Maximum savings unlocked · You're saving 20%";
        } else if (selectedBundleQty === 5) {
          message.textContent =
            '15% savings unlocked · Choose 10+ packs to save 20%';
        } else {
          message.textContent =
            '10% savings unlocked · Choose 5 packs to save 15%';
        }
      }

      if (compareNode) {
        compareNode.textContent =
          formatMoney(originalTotal, currency);
      }

      if (totalNode) {
        totalNode.textContent =
          formatMoney(discountedTotal, currency);
      }

      if (priceRow) {
        priceRow.hidden = false;
      }
    };

    const setBundleQuantity = (qty) => {
      const quantityInput = getQuantityInput();

      if (!quantityInput) return;

      selectedBundleQty = qty;

      quantityInput.value = qty;

      quantityInput.dispatchEvent(
        new Event('input', { bubbles: true })
      );

      quantityInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );

      renderSelectedBundle();
    };

    buttons.forEach((button) => {
      if (button.dataset.edenBound === 'true') return;

      button.dataset.edenBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();

        const qty = Number(button.dataset.bundleQty);

        if (!tiers[qty]) return;

        setBundleQuantity(qty);
      });
    });

    const quantityInput = getQuantityInput();

    if (quantityInput && quantityInput.dataset.edenBound !== 'true') {
      quantityInput.dataset.edenBound = 'true';

      const manualQuantityChange = () => {
        if (!selectedBundleQty) return;

        const currentQty = Number(quantityInput.value || 1);

        if (currentQty !== selectedBundleQty) {
          clearSelection();
        }
      };

      quantityInput.addEventListener('input', manualQuantityChange);
      quantityInput.addEventListener('change', manualQuantityChange);
    }

    if (productRoot && productRoot.dataset.edenVariantBound !== 'true') {
      productRoot.dataset.edenVariantBound = 'true';

      productRoot.addEventListener('change', () => {
        if (!selectedBundleQty) return;

        window.setTimeout(renderSelectedBundle, 50);
        window.setTimeout(renderSelectedBundle, 200);
      });
    }

    /* IMPORTANT:
       No default bundle selection.
       Quantity remains the normal Shopify quantity until customer clicks
       2 / 5 / 10 packs.
    */
    clearSelection();
  };

  const init = (scope = document) => {
    scope
      .querySelectorAll('[data-eden-bundle]')
      .forEach(initBundle);

    scope
      .querySelectorAll('[data-eden-delivery]')
      .forEach(initDelivery);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  window.addEventListener('pageshow', () => {
    init(document);
  });
})();